import { useEffect, useState } from "react";
import { useT } from "../i18n/context";
import { useStore } from "../store";
import { VEIL_AFTER_MS, veilVisible } from "../ws/liveness";
import { Icon } from "./icons";

/** Re-renders once the veil falls due, so a drop that lasts covers the table by itself. */
function useDueAt(open: boolean, connectionAt: number): void {
  const [, redraw] = useState(0);
  useEffect(() => {
    if (open) return;
    const left = connectionAt + VEIL_AFTER_MS - Date.now();
    if (left <= 0) return;
    const timer = window.setTimeout(() => redraw((n) => n + 1), left);
    return () => window.clearTimeout(timer);
  }, [open, connectionAt]);
}

/**
 * The socket dropped (design 9): the screen dims and blurs until it is back.
 * When you asked to leave, it says that instead: a leave is not a lost connection.
 * A drop waits a moment first (review 0005 S1); a leave you asked for shows at once.
 */
export function Reconnecting({ seated }: { seated: boolean }) {
  const { t } = useT();
  const connection = useStore((s) => s.connection);
  const connectionAt = useStore((s) => s.connectionAt);
  const leaving = useStore((s) => s.leaving);
  const open = connection === "open";
  useDueAt(open, connectionAt);
  if (open) return null;
  if (!leaving && !veilVisible(open, connectionAt, Date.now())) return null;
  return (
    <div
      role="alert"
      className="absolute inset-0 z-40 flex items-center justify-center bg-ground/55 backdrop-blur-[3px]"
    >
      <div className="flex flex-col items-center gap-4 rounded-[22px] border-[1.5px] border-edge bg-panel-strong px-[34px] py-[26px] shadow-panel">
        <span
          aria-hidden="true"
          className="h-9 w-9 animate-spin rounded-full border-[3px] border-ink/20 border-t-gold"
        />
        <span className="text-[17px] font-extrabold">
          {leaving ? t("table.leaving") : t("table.connecting")}
        </span>
        {seated && !leaving ? (
          <span className="max-w-[280px] text-center text-[13px] text-ink/65">
            {t("table.connecting.sub")}
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Where a spectator's hand would be (design 10). */
export function Watching({ dense }: { dense: boolean }) {
  const { t } = useT();
  return (
    <div className="flex h-full items-start justify-center pt-2">
      <span className="flex items-center gap-2.5 rounded-pill border-[1.5px] border-edge bg-panel-strong px-[18px] py-2.5">
        <Icon name="eye" size={18} />
        <span className="text-[14px] font-bold">{t("table.watching")}</span>
        {dense ? null : <span className="text-[12px] text-ink/60">{t("table.watching.sub")}</span>}
      </span>
    </div>
  );
}
